import { FC } from "react";
import { SegmentPopulation } from "@models/population.model";
import { networkLabel } from "@/shared/enums/networkColors";
import ChannelHoldouts from "./ChannelHoldouts";
import Disclosure from "./Disclosure";
import HoldoutBadge from "./HoldoutBadge";
import { splitLabel } from "./TieredConversions";

/*
    The Holdouts section (MPN-2077): the population's two tiers, as one picture and two cards.

    Tier 1 is the baseline holdout: a percentage of the whole population held out of every
    channel, the control group every lift number is measured against. Tier 2 is a channel
    holdout (MPN-2060): a split of the members Tier 1 left, held out of one channel only.
    The bars show the layering, the connector shows the second tier drawing from the first's
    remainder, and once a tier is applied and published it is locked: a change then would
    re-draw a measurement already running.
*/

/** The baseline holdout's draft, owned by the roster so a reload resets it. */
export interface BaselineDraft {
  enabled: boolean;
  pct: number;
  onEnabledChange: (enabled: boolean) => void;
  onPctChange: (pct: number) => void;
  isDirty: boolean;
  onApply: () => void;
}

interface Props {
  data: SegmentPopulation;
  draft: BaselineDraft;
  saving: boolean;
  channelHoldoutsOn: boolean;
  onApplyChannel: (channel: string, enabled: boolean, pct: number) => Promise<void>;
}

const n = (value: number) => value.toLocaleString();
const pctOf = (count: number, whole: number) => (whole > 0 ? `${(count / whole) * 100}%` : "0%");

const HoldoutsSection: FC<Props> = ({ data, draft, saving, channelHoldoutsOn, onApplyChannel }) => {
  // The bars are real people: test rows are neither served nor held out.
  const real = data.total_population - data.test_count;
  const control = data.control_count;
  const served = data.served_count;
  const applied = data.control_group_enabled && control > 0;
  const locked = applied && data.published === true;
  const channels = channelHoldoutsOn ? data.channel_holdouts ?? [] : [];
  const live = channels.filter((h) => h.enabled);

  /*
      How large the holdout can actually get. Anyone already added to an audience has been
      handed to an ad network and can no longer answer the lift question, so assignment
      skips them (MPN-1523). Default to 0 so a server that predates these fields simply
      shows nothing rather than "NaN of NaN".
  */
  const ineligible = data.holdout_ineligible_count ?? 0;
  const eligible = data.holdout_eligible_count ?? 0;
  const total = ineligible + eligible;

  const channelLabel = (h: { label: string; channel: string }) => h.label || networkLabel(h.channel);
  const summary = [
    `Baseline ${data.control_group_pct}% (${n(control)} members, ${
      applied ? (data.published ? "applied · published" : "applied")
        : data.control_group_enabled ? "not yet applied" : "off"
    })`,
    ...channels.map(
      (h) => `${channelLabel(h)} channel holdout ${splitLabel(h.pct)} (${
        !h.enabled ? "off" : h.published ? "applied · published" : "applied"
      })`,
    ),
    ...(channelHoldoutsOn && channels.length === 0 ? ["no channel holdout"] : []),
  ].join(" · ");

  return (
    <Disclosure title="Holdouts" defaultOpen summary={summary} className="population-disclosure--holdouts">
      <div className="data-file__note mb-3">
        {channelHoldoutsOn
          ? "Two tiers: a baseline holdout for all channels, and a channel test carved from the remaining members."
          : "A baseline holdout, held out of every channel."}
      </div>

      <div className="holdout-bars">
        <div className="holdout-bar" role="img" aria-label={`Holdout ${n(control)} · Treatment ${n(served)} served`}>
          <div
            className="holdout-bar__seg holdout-bar__seg--control"
            style={{ width: pctOf(control, real) }}
            title={`Holdout · ${n(control)}`}
          >
            {n(control)}
          </div>
          <div className="holdout-bar__seg holdout-bar__seg--treatment">Treatment · {n(served)} served</div>
        </div>
        <div className="holdout-bar__legend">Holdout · {n(control)}</div>
        {live.map((h) => {
          const label = channelLabel(h);
          const exposed = h.treatment_count - h.held_out_count;
          return (
            <div
              key={h.channel}
              className="holdout-bar holdout-bar--channel"
              role="img"
              aria-label={`${label}: channel treatment ${n(exposed)} · channel holdout ${n(h.held_out_count)}`}
            >
              {/* Aligned under the top bar: the baseline holdout's width, then the split
                  of what it left. */}
              <div
                className="holdout-bar__seg holdout-bar__seg--spacer"
                style={{ width: pctOf(control, real) }}
                title="Baseline holdout"
              />
              <div
                className="holdout-bar__seg holdout-bar__seg--exposed"
                style={{ width: pctOf(exposed, real) }}
                title={`Channel treatment · ${label} · ${n(exposed)}`}
              >
                Channel treatment · {label} · {n(exposed)}
              </div>
              <div
                className="holdout-bar__seg holdout-bar__seg--held"
                style={{ width: pctOf(h.held_out_count, real) }}
                title={`Channel holdout · ${label} · ${n(h.held_out_count)}`}
              >
                Channel holdout · {label} · {n(h.held_out_count)}
              </div>
            </div>
          );
        })}
        <div className="data-file__note">
          {n(real)} total members · baseline holdout {data.control_group_pct}%
          {channelHoldoutsOn && (
            <>
              {" "}· {n(served)} eligible for the channel holdout
              {live.length === 1 ? ` (${splitLabel(live[0].pct)})` : ""}
            </>
          )}
        </div>
      </div>

      <div className="holdout-tiers">
        <div
          className={`holdout-tier${locked ? " holdout-tier--locked" : ""}`}
          data-tier="baseline"
          aria-disabled={locked || undefined}
        >
          <div className="holdout-tier__head">
            <span className="holdout-tier__num">Tier 1</span>
            <span className="holdout-tier__name">Baseline holdout</span>
            <span className="ms-auto">
              <HoldoutBadge
                status={locked ? "locked" : applied ? "applied" : data.control_group_enabled ? "pending" : "off"}
              />
            </span>
          </div>
          <label className="holdout__toggle">
            <input
              type="checkbox"
              checked={draft.enabled}
              onChange={(e) => draft.onEnabledChange(e.target.checked)}
              disabled={saving || locked}
            />
            <span>Hold out a control group</span>
          </label>
          <div className="data-file__note">
            Excluded from ad delivery on every channel.
            {locked
              ? " Applied and published, so it can no longer be changed."
              : " Assignment is frozen per consumer — raising the percentage tops the group up, it does not reshuffle it."}
          </div>
          <div className="holdout-tier__controls">
            {draft.enabled && (
              <div className="holdout__pct">
                <input
                  type="range"
                  min={1}
                  max={50}
                  value={draft.pct}
                  aria-label="Baseline holdout percentage"
                  onChange={(e) => draft.onPctChange(Number(e.target.value))}
                  disabled={saving || locked}
                />
                <span className="holdout__pct-value">{draft.pct}%</span>
              </div>
            )}
            <div className="holdout-tier__figure">
              <div className="holdout-tier__figure-label">Members held out</div>
              <div className="holdout-tier__figure-value">{n(control)}</div>
            </div>
            <button
              type="button"
              className="btn btn-sm btn-outline-primary ms-auto"
              onClick={draft.onApply}
              disabled={locked || !draft.isDirty || saving}
              style={{ fontSize: "0.72rem" }}
            >
              {locked ? "Applied" : saving ? "Applying…" : "Apply"}
            </button>
          </div>
          {/*
              Without this, a population whose members are already on an ad network applies a
              percentage and gets a smaller holdout than it asked for, with nothing on screen
              explaining why (MPN-1523). Anyone already handed to an ad network cannot answer
              the lift question, so they are skipped — that is the feature working, not failing.
              Members of an audience that has not been published do not count (MPN-1729).
          */}
          {ineligible > 0 && (
            <div className="data-file__note mt-2">
              <span className="population-holdout__limit">
                {n(ineligible)} of {n(total)}{" "}
                {ineligible === 1 ? "person has" : "people have"} already been sent to an ad
                network and can no longer be held out, so the group can hold at most{" "}
                {n(eligible)}.
              </span>
            </div>
          )}
        </div>

        {channelHoldoutsOn && (
          <ChannelHoldouts
            holdouts={channels}
            treatmentCount={served}
            saving={saving}
            onApply={onApplyChannel}
          />
        )}
      </div>

      <div className="data-file__note">
        Holdout members are excluded from ad delivery so lift can be measured. Assignment is
        frozen per consumer — the baseline holdout and the channel treatment and channel holdout
        groups do not reshuffle when percentages change. Members already sent to an ad network
        can no longer be held out.
      </div>
    </Disclosure>
  );
};

export default HoldoutsSection;
